import { AWAKEN, FEAT, FEATS, TICK_HZ, UNIQUES, featGoal, legendUses, type UniqueId } from '@somnhold/content';
import { featAllies, featDone, type CampaignState, type HeroState, type ItemInstance } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';

/*
 * The feats of a unique piece (docs/UNICAS.md §2, §6): where the piece is on its way, shown on its sheet and at the forge.
 * The four stages as a track (awake ones lit, the one being earned outlined), what the piece wants now with its bar, and
 * a slow beat once the next stage's feat is done, until it is awakened. Plain DOM in the bastion's sheets: nothing here
 * runs in a fight or every frame.
 */

const ROMAN = ['', 'I', 'II', 'III', 'IV'];

/** The Chasm floor a hero's feats count from (a third of its level). */
const simaFrom = (h0: HeroState): number => Math.trunc(h0.level / 3);

/** How many at one go a piece's test asks (FEAT.need), by piece. */
const NEED: Partial<Record<UniqueId, keyof typeof FEAT.need>> = {
  gateBlade: 'bash', garrisonKey: 'triangle', fallenStaff: 'staff', watchBow: 'pierce', wallAxe: 'whirl', oldRootStaff: 'roots', lastWatch: 'challenge',
  anvilHarness: 'burst', frostMantle: 'mantle', oathPlate: 'aegis', dwarfBark: 'bear', stormEye: 'storm', mossWhistle: 'rain', tailFang: 'chain', vigilCandle: 'ink',
};
/** How long a test waits or holds, in seconds, by piece (the lamp's ally, the smite's elite, the allies inside). */
const SECS: Partial<Record<UniqueId, number>> = { chapelLamp: FEAT.watch.lamp, gasparHammer: FEAT.watch.smite, dwarfMail: FEAT.allyHold.dwarfMail ?? 0, maelisSeed: FEAT.allyHold.maelisSeed ?? 0 };

/** What a piece's feat at a step asks, in words: the count and every number of its test from the content. */
export const featGoalText = (s: CampaignState, h0: HeroState, id: UniqueId, step: number): string => {
  const m = featGoal(id, step);
  const need = NEED[id];
  const vars = {
    m,
    a: featAllies(s, id) || (FEATS[id].allies ?? 0),
    k: need ? FEAT.need[need] : 0,
    sec: Math.round((SECS[id] ?? 0) / TICK_HZ),
    p: FEAT.watch.lampBelowBp / 100,
    lo: FEAT.need.lowBp / 100,
    hi: FEAT.need.highBp / 100,
  };
  if (step === 1) return t(`feat.${id}.1`, vars);
  if (step === 2) return t(`feat.${id}.2`, vars);
  if (step === 3) return t('feat.deep', { goal: t(`feat.${id}.2`, vars) });
  return t('feat.legend', { m, k: legendUses(id) });
};

/** Whether the piece's next stage has its feat done and waits only for the forge (it beats on the sheet until then). */
export const featReady = (h0: HeroState, item: ItemInstance | undefined): boolean => !!item?.unique && (item.awaken ?? 0) < AWAKEN.stages.length && featDone(h0, item);

/** The level the piece's next stage asks, when the hero isn't there yet (null: it is). */
export const featLevelWait = (h0: HeroState, item: ItemInstance): number | null => {
  const lv = AWAKEN.stages[item.awaken ?? 0]?.level ?? 0;
  return h0.level < lv ? lv : null;
};

/** What a done feat says: ready at the forge, or ready and waiting for the level the stage asks. */
export const featReadyText = (h0: HeroState, item: ItemInstance): string => {
  const n = ROMAN[(item.awaken ?? 0) + 1] ?? '';
  const lv = featLevelWait(h0, item);
  return lv !== null ? t('feat.readyAt', { n, l: lv }) : t('feat.ready', { n });
};

/** How a felled big boss reads in the Legend's list. */
const bossName = (key: string): string => t(`enemy.${key.startsWith('un:') ? key.slice(3) : key}`);

/**
 * The piece's way (§6): its four stages, the feat it is on with its bar, and a beat when the next stage can be awakened.
 * `compact`: only the goal and the bar (the forge's list already says the rest).
 */
export const featBlock = (s: CampaignState, h0: HeroState, item: ItemInstance): HTMLElement | '' => {
  const id = item.unique;
  if (!id || UNIQUES[id].cls !== h0.heroClass) return '';
  const f = h0.feats?.[id];
  const awake = item.awaken ?? 0;
  const ready = featReady(h0, item);
  const step = f?.step ?? 1;
  // The stages: lit up to the awake one; the next one ready (its feat done) or being earned.
  const track = h(
    'ol.feat-track',
    { 'aria-label': t('feat.stages') },
    ...[1, 2, 3, 4].map((n) => {
      const state = n <= awake ? 'awake' : n === awake + 1 && ready ? 'ready' : n === step ? 'now' : n < step ? 'earned' : 'later';
      return h(`li.feat-pip.${state}`, { title: `${ROMAN[n]} · ${t(`feat.stage.${n}`)}` }, h('span.feat-gem', { 'aria-hidden': 'true' }), h('span.feat-roman', {}, ROMAN[n] ?? ''));
    }),
  );
  const box = h(`div.feat-card${ready ? '.ready' : ''}`, { 'data-test': 'feat', 'data-step': String(step) }, track);
  if (!f) box.append(h('p.feat-goal', {}, t('feat.none')));
  else if (f.step > 4) box.append(h('p.feat-goal.done', {}, t('feat.all')));
  else {
    const goal = featGoal(id, f.step);
    const n = Math.min(f.n, goal);
    const pct = goal > 0 ? Math.round((n * 100) / goal) : 0;
    box.append(
      h('div.feat-label', {}, t('feat.of', { n: f.step, s: t(`feat.stage.${f.step}`) })),
      h('p.feat-goal', {}, h('b', {}, `${t(`unique.${id}.wants`)}: `), featGoalText(s, h0, id, f.step)),
      h(
        'div.feat-bar',
        { role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': String(goal), 'aria-valuenow': String(n) },
        h('i', { style: `width:${pct}%` }),
        h('span.feat-num', {}, `${String(n)} / ${String(goal)}`),
      ),
    );
    if (f.step === 3) box.append(h('p.feat-hint', {}, t('feat.deepHint', { f: simaFrom(h0) })));
    if (f.step === 4) {
      box.append(h('p.feat-hint', {}, t('feat.legendHint', { f: simaFrom(h0) })));
      if (f.bosses?.length) box.append(h('p.feat-hint.felled', {}, t('feat.felled', { list: f.bosses.map(bossName).join(' · ') })));
    }
  }
  if (ready) box.append(h('p.feat-ready', { 'data-test': 'feat-ready' }, featReadyText(h0, item)));
  else if (f && f.step <= 4) box.append(h('p.feat-hint.where', {}, t('feat.where')));
  return box;
};

/** The forge's first need (§6): the feat of the next stage, ticked when done, grey with what is left when not. */
export const featNeed = (s: CampaignState, h0: HeroState, item: ItemInstance): HTMLElement | '' => {
  const id = item.unique;
  if (!id) return '';
  const stage = (item.awaken ?? 0) + 1;
  const f = h0.feats?.[id];
  const goalText = featGoalText(s, h0, id, stage);
  if (featDone(h0, item)) return h('li.ok.feat-need', { 'data-test': 'feat-need' }, h('span.mark', { 'aria-hidden': 'true' }, '✓'), t('uawaken.featDone', { goal: goalText }));
  const goal = featGoal(id, stage);
  const n = f && f.step === stage ? Math.min(f.n, goal) : 0;
  const pct = goal > 0 ? Math.round((n * 100) / goal) : 0;
  return h(
    'li.no.feat-need.pending',
    { 'data-test': 'feat-need' },
    h('span.mark', { 'aria-hidden': 'true' }, '✗'),
    h('span.feat-need-body', {}, t('uawaken.needFeat', { goal: goalText, n: String(n), m: String(goal) }), h('span.feat-bar.thin', {}, h('i', { style: `width:${pct}%` }))),
  );
};

/**
 * The wave's report (§2.3): what my spectre added to my pieces' feats while I wasn't there, piece by piece, each with
 * where its feat stands now (full when that feat is already done).
 */
export const featGains = (s: CampaignState, h0: HeroState, gains: { unique: UniqueId; step: number; n: number }[]): HTMLElement =>
  h(
    'section.feat-gains',
    { 'data-test': 'feat-gains' },
    h('h5', {}, t('feat.ghostTitle')),
    ...gains.map((g) => {
      const m = featGoal(g.unique, g.step);
      const f = h0.feats?.[g.unique];
      const have = f && f.step === g.step ? Math.min(f.n, m) : m;
      const pct = m > 0 ? Math.round((have * 100) / m) : 0;
      return h(
        `div.feat-gain${have >= m ? '.full' : ''}`,
        {},
        h('span.feat-gain-text', {}, t('feat.ghost', { u: t(`unique.${g.unique}.short`), n: String(g.n), have: String(have), m: String(m) })),
        h('span.feat-bar.thin', {}, h('i', { style: `width:${pct}%` })),
      );
    }),
  );
